/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, ButtonGroup, Dialog, Typography2 } from '@coursera/cds-core';
import { DownloadIcon, ErrorIcon, SuccessIcon } from '@coursera/cds-icons';

import StatusCard from 'bundles/enterprise-admin-learners/components/invite-learners/StatusCard';
import { downloadInviteErrorReport } from 'bundles/enterprise-admin-learners/components/invite-learners/downloadInviteErrorReport';
import type { FailedInvite } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  statusCardList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  downloadButton: css`
    align-self: flex-start;
    padding-left: 0;
  `,
};

export type PartialInviteSuccessDialogProps = {
  open: boolean;
  onClose: () => void;
  invitedCount: number;
  failed: FailedInvite[];
  groupName: string;
  canViewGroup: boolean;
  onViewGroup: () => void;
};

/** Shown when some invites succeeded and some failed — a per-category breakdown plus an error report. */
const PartialInviteSuccessDialog = ({
  open,
  onClose,
  invitedCount,
  failed,
  groupName,
  canViewGroup,
  onViewGroup,
}: PartialInviteSuccessDialogProps) => {
  const failedCount = failed.length;
  const totalCount = invitedCount + failedCount;
  const invitedCountLabel = invitedCount === 1 ? _t('1 learner') : _t('#{invitedCount} learners', { invitedCount });

  return (
    <Dialog open={open} onClose={onClose} width="medium">
      <HeadingGroup>
        {_t('#{invitedCount} out of #{totalCount} learners invited and added to #{groupName}', {
          invitedCount,
          totalCount,
          groupName,
        })}
      </HeadingGroup>
      <Content css={styles.content}>
        <Typography2 component="p" color="supportText">
          {_t("Invites are on their way. Here's the breakdown.")}
        </Typography2>
        {/* Always-present live region so screen readers announce the failure breakdown when it appears. */}
        <div role="alert" css={styles.statusCardList}>
          <Typography2 component="h3" variant="subtitleSmall" color="supportText">
            {_t('Invitation')}
          </Typography2>
          <StatusCard
            title={_t('Skipped')}
            detail={
              failedCount === 1
                ? _t('1 entry had missing or invalid data')
                : _t('#{failedCount} entries had missing or invalid data', { failedCount })
            }
            detailColor="error"
            icon={<ErrorIcon color="error" />}
          />
          <StatusCard title={_t('Invites sent')} detail={invitedCountLabel} icon={<SuccessIcon color="success" />} />
        </div>
        <div css={styles.statusCardList}>
          <Typography2 component="h3" variant="subtitleSmall" color="supportText">
            {_t('Group Assignment')}
          </Typography2>
          <StatusCard title={groupName} detail={invitedCountLabel} icon={<SuccessIcon color="success" />} />
        </div>
        <Button
          variant="ghost"
          size="small"
          icon={<DownloadIcon />}
          iconPosition="before"
          onClick={() => downloadInviteErrorReport(failed)}
          css={styles.downloadButton}
        >
          {_t('Download error report')}
        </Button>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{ onClick: onViewGroup, disabled: !canViewGroup, children: _t('View all learners') }}
          alternateButtonProps={{ variant: 'secondary', onClick: onClose, children: _t('Done') }}
        />
      </Actions>
    </Dialog>
  );
};

export default PartialInviteSuccessDialog;
